import { useState } from "react";
import { trpc } from "@/admin/lib/trpc";
import { Button } from "@nosible/shared/components/ui/button";
import { Mail, CheckCircle, XCircle, Clock, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router";

interface PaginatedEmailListProps {
  jobId?: string;
  status?: "pending" | "sent" | "failed";
  startDate?: Date;
  endDate?: Date;
}

export function PaginatedEmailList({
  jobId,
  status,
  startDate,
  endDate,
}: PaginatedEmailListProps) {
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const { data, isLoading, error } = trpc.email.listEmails.useQuery({
    page,
    pageSize,
    jobId,
    status,
    startDate,
    endDate,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-chromium-mist">Loading emails...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-signal-blue/20 rounded-lg text-silica">
        Error loading emails: {error.message}
      </div>
    );
  }

  if (!data || data.emails.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-chromium-mist">
        <Mail className="h-12 w-12 mb-3 opacity-50" />
        <p>No emails found</p>
      </div>
    );
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "sent":
        return <CheckCircle className="h-4 w-4 text-neon-green" />;
      case "failed":
        return <XCircle className="h-4 w-4 text-signal-blue" />;
      case "pending":
        return <Clock className="h-4 w-4 text-chromium-mist" />;
      default:
        return null;
    }
  };

  const formatDate = (date: Date | string | null) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="space-y-4">
      <div className="bg-slate-core rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-obsidian">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-silica">
                Recipient
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-silica">
                Subject
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-silica">
                Type
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-silica">
                Status
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-silica">
                Sent At
              </th>
              {!jobId && (
                <th className="px-4 py-3 text-left text-sm font-medium text-silica">
                  Job
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-chromium-mist/10">
            {data.emails.map((email) => (
              <tr key={email.id} className="hover:bg-obsidian/50 transition-colors">
                <td className="px-4 py-3 text-sm text-silica">
                  {email.recipientEmail}
                </td>
                <td className="px-4 py-3 text-sm text-chromium-mist max-w-xs truncate">
                  {email.subject}
                </td>
                <td className="px-4 py-3 text-sm text-chromium-mist">
                  {email.emailType}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(email.status)}
                    <span className="text-sm text-chromium-mist capitalize">
                      {email.status}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-chromium-mist">
                  {formatDate(email.sentAt || email.createdAt)}
                </td>
                {!jobId && (
                  <td className="px-4 py-3 text-sm">
                    {email.jobId ? (
                      <Link
                        to={`/emails/jobs/${email.jobId}`}
                        className="text-neon-green hover:underline"
                      >
                        View Job
                      </Link>
                    ) : (
                      <span className="text-chromium-mist/50">-</span>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-chromium-mist">
            Page {data.pagination.page} of {data.pagination.totalPages} (
            {data.pagination.total} total)
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              variant="outline"
              size="sm"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <Button
              onClick={() => setPage((p) => Math.min(data.pagination.totalPages, p + 1))}
              disabled={page === data.pagination.totalPages}
              variant="outline"
              size="sm"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
